/**
 * The mix of review DECISIONS for the period.
 *
 * Never titled or described as model accuracy. These are the outcomes operators
 * recorded — a false-alert share says how often people judged a detection not to
 * be real, which is not the same as the model being wrong, and framing it as
 * accuracy would turn a workload view into a performance claim about the model.
 *
 * Six categories is at the limit of what a ring can carry, so every slice is
 * also listed with its label and count beside the chart: the ring shows shape,
 * the list is the readable version and doubles as the table view the contrast
 * check requires.
 */
import { useTranslation } from "react-i18next";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import type { AlertAnalyticsOutcomeItem } from "@/alerts/types/alerts-analytics";

/**
 * Fixed hue order — a slice keeps its color regardless of which categories are
 * present, so a period with no duplicates does not repaint the others.
 * `unclassified_legacy` is intentionally NEUTRAL: it is an absence of a
 * classification, not a category of decision.
 */
type OutcomeLabelKey =
  | "outcomes.confirmedSignal"
  | "outcomes.falseAlert"
  | "outcomes.duplicate"
  | "outcomes.insufficientEvidence"
  | "outcomes.other"
  | "outcomes.unclassifiedLegacy";

const OUTCOME_STYLE: Record<string, { labelKey: OutcomeLabelKey; color: string }> = {
  confirmed_signal: { labelKey: "outcomes.confirmedSignal", color: "hsl(var(--chart-1))" },
  false_alert: { labelKey: "outcomes.falseAlert", color: "hsl(var(--chart-2))" },
  duplicate: { labelKey: "outcomes.duplicate", color: "hsl(var(--chart-3))" },
  insufficient_evidence: {
    labelKey: "outcomes.insufficientEvidence",
    color: "hsl(var(--chart-4))",
  },
  other: { labelKey: "outcomes.other", color: "hsl(var(--chart-5))" },
  unclassified_legacy: {
    labelKey: "outcomes.unclassifiedLegacy",
    color: "hsl(var(--muted-foreground))",
  },
};

/** An outcome code the taxonomy does not name yet still has to render. */
const colorFor = (code: string) => OUTCOME_STYLE[code]?.color ?? "hsl(var(--muted-foreground))";

export const AlertOutcomesChart = ({
  items,
  reviewed,
  legacyDefaultedCount,
}: {
  items: AlertAnalyticsOutcomeItem[];
  reviewed: number;
  /** Dispositions derived from legacy data rather than chosen by an operator. */
  legacyDefaultedCount: number;
}) => {
  const { t } = useTranslation("alerts");
  const present = items.filter((item) => item.count > 0);

  // An unknown code has no key to look up, so its raw token is made readable
  // rather than dropped — the same rule the rest of the module follows.
  const labelFor = (code: string) =>
    OUTCOME_STYLE[code] ? t(OUTCOME_STYLE[code].labelKey) : code.replace(/_/g, " ");

  if (present.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center rounded-md border border-dashed border-border text-xs text-muted-foreground">
        {t("outcomesChart.empty")}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-4">
      <div className="relative h-40 w-40 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={present}
              dataKey="count"
              nameKey="code"
              innerRadius={46}
              outerRadius={68}
              // A 2px surface gap keeps adjacent slices from reading as one.
              paddingAngle={2}
              strokeWidth={2}
              stroke="hsl(var(--background))"
              isAnimationActive={false}
            >
              {present.map((item) => (
                <Cell key={item.code} fill={colorFor(item.code)} />
              ))}
            </Pie>
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const item = payload[0].payload as AlertAnalyticsOutcomeItem;
                return (
                  <div className="rounded-md border border-border bg-background px-2.5 py-1.5 text-xs shadow-md">
                    <span className="text-muted-foreground">{labelFor(item.code)}</span>{" "}
                    <span className="font-medium tabular-nums">{item.count}</span>
                  </div>
                );
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        {/* The hero number the ring is a breakdown of. */}
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-semibold tabular-nums">{reviewed}</span>
          <span className="text-[10px] text-muted-foreground">{t("outcomesChart.reviewed")}</span>
        </div>
      </div>

      <ul className="min-w-0 flex-1 space-y-1">
        {present.map((item) => {
          return (
            <li key={item.code} className="flex items-center gap-2 text-xs">
              <span
                aria-hidden
                className="inline-block h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: colorFor(item.code) }}
              />
              <span className="truncate text-muted-foreground">{labelFor(item.code)}</span>
              <span className="ml-auto shrink-0 font-medium tabular-nums">{item.count}</span>
              {item.share != null && (
                <span className="w-10 shrink-0 text-right tabular-nums text-muted-foreground">
                  {Math.round(item.share * 100)}%
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {legacyDefaultedCount > 0 && (
        <p className="w-full text-[11px] text-muted-foreground">
          {t("outcomesChart.legacyNote", { n: legacyDefaultedCount })}
        </p>
      )}
    </div>
  );
};
